// src/components/GRAIEditor/index.jsx
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import "./GRAIEditor.css";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Composants internes
import GridHeader from "./GridHeader";
import GridCell from "./GridCell";
import ArrowManager from "./ArrowManager";
import Toolbar from "./Toolbar";
import ExternalSource from "./ExternalSource";

const GRAIEditor = ({
  instanceId = "grai_default",
  storageKey: customStorageKey,
  title = "Grille GRAI",
}) => {
  // 🔑 Clé de stockage propre à chaque instance
  const storageKey = useMemo(
    () => customStorageKey || `graiGridData_${instanceId}`,
    [customStorageKey, instanceId]
  );

  // --------------------
  // ÉTATS PRINCIPAUX
  // --------------------
  const [columns, setColumns] = useState([
    {
      id: 1,
      title: "Informations externes",
      isPseudoFunction: true,
      isObligatory: true,
      isFixed: true,
    },
    { id: 2, title: "Gérer les produits", isObligatory: true },
    { id: 3, title: "Planifier", isObligatory: true },
    { id: 4, title: "Gérer les ressources", isObligatory: true },
    {
      id: 5,
      title: "Informations internes",
      isPseudoFunction: true,
      isObligatory: true,
      isFixed: true,
    },
  ]);

  const [rows, setRows] = useState([
    { id: 1, title: "2 ans", horizon: "H1", period: 730 },
    { id: 2, title: "1 an", horizon: "H2", period: 365 },
    { id: 3, title: "6 mois", horizon: "H3", period: 180 },
    { id: 4, title: "1 mois", horizon: "H4", period: 30 },
  ]);

  const [cells, setCells] = useState({});
  const [arrows, setArrows] = useState([]);
  const [externalSources, setExternalSources] = useState([
    { id: 1, position: "top", title: "Prévisions commerciales" },
    { id: 2, position: "left", title: "Historique des ventes" },
    { id: 3, position: "right", title: "Stocks PF" },
  ]);

  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(100);

  const [isDragging, setIsDragging] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });

  const gridRef = useRef(null);
  const gridContentRef = useRef(null);
  const arrowManagerRef = useRef(null);

  // "cell", "arrow-info", "arrow-decision", "delete"
  const [editorMode, setEditorMode] = useState("cell");
  const [arrowStart, setArrowStart] = useState(null);

  const [notification, setNotification] = useState(null);
  const [connectedCells, setConnectedCells] = useState({});

  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });

  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [automaticValidation, setAutomaticValidation] = useState(true);

  // --- Sous-éditeur ---
  const [activeCell, setActiveCell] = useState(null);
  const [cellDiagrams, setCellDiagrams] = useState({});
  const [subNodes, setSubNodes] = useState([]);
  const [subEdges, setSubEdges] = useState([]);

  useEffect(() => {
    console.log(`🧩 [${instanceId}] Cellule active :`, activeCell);
  }, [activeCell, instanceId]);

  const [arrowTooltip, setArrowTooltip] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
    arrowId: null,
  });

  // Sécurité anti-NaN tooltips
  useEffect(() => {
    if (!Number.isFinite(tooltip.x) || !Number.isFinite(tooltip.y)) {
      console.warn("🧩 Tooltip invalide détecté :", tooltip);
      setTooltip((prev) => ({ ...prev, x: 0, y: 0 }));
    }
    if (!Number.isFinite(arrowTooltip.x) || !Number.isFinite(arrowTooltip.y)) {
      console.warn("🧩 ArrowTooltip invalide détecté :", arrowTooltip);
      setArrowTooltip((prev) => ({ ...prev, x: 0, y: 0 }));
    }
  }, [tooltip, arrowTooltip]);

  const normalize = (text) =>
    text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // --------------------
  // AJOUT / MODIF COLONNES / LIGNES
  // --------------------
  const addColumn = (title, position = "end") => {
    const newColumn = {
      id: Date.now(),
      title,
      type: "Bonus",
      color: "#FACC15",
      isBonus: true,
    };

    setColumns((prevColumns) => {
      const updated = [...prevColumns];

      const indexProduct = updated.findIndex(
        (col) =>
          col.title?.toLowerCase().includes("gérer les produits") ||
          col.title?.toLowerCase().includes("gérer produits")
      );
      const indexResource = updated.findIndex(
        (col) =>
          col.title?.toLowerCase().includes("gérer les ressources") ||
          col.title?.toLowerCase().includes("gérer ressources")
      );

      if (position === "beforeProduct" && indexProduct !== -1) {
        updated.splice(indexProduct, 0, newColumn);
      } else if (position === "afterResource" && indexResource !== -1) {
        updated.splice(indexResource + 1, 0, newColumn);
      } else {
        updated.push(newColumn);
      }

      return updated;
    });

    showNotification(`Colonne "${title}" ajoutée (type Bonus)`, "success");
  };

  const addRow = (title, horizon) => {
    let period = 0;
    const periodMatch = title
      .toLowerCase()
      .match(/(\d+)\s*(an|ans|mois|semaine|semaines|jour|jours|heure|heures)/);

    if (periodMatch) {
      const value = parseInt(periodMatch[1]);
      const unit = periodMatch[2];

      switch (unit) {
        case "an":
        case "ans":
          period = value * 365;
          break;
        case "mois":
          period = value * 30;
          break;
        case "semaine":
        case "semaines":
          period = value * 7;
          break;
        case "jour":
        case "jours":
          period = value;
          break;
        case "heure":
        case "heures":
          period = value / 24;
          break;
        default:
          period = 0;
      }
    }

    let insertIndex = 0;
    while (insertIndex < rows.length && rows[insertIndex].period >= period) {
      insertIndex++;
    }

    const newRowId = Math.max(...rows.map((row) => row.id), 0) + 1;
    const newRow = { id: newRowId, title, horizon, period };

    const newRows = [...rows];
    newRows.splice(insertIndex, 0, newRow);
    setRows(newRows);

    if (automaticValidation) {
      setTimeout(() => validateGraiRules(), 0);
    }
  };

  const updateCell = (rowId, colId, content) => {
    setCells((prev) => ({
      ...prev,
      [`${rowId}-${colId}`]: {
        ...prev[`${rowId}-${colId}`],
        content,
      },
    }));

    const cellElement = document.getElementById(`cell-${rowId}-${colId}`);
    if (cellElement) {
      cellElement.classList.add("animate-pulse");
      setTimeout(() => {
        cellElement.classList.remove("animate-pulse");
      }, 1500);
    }
  };

  // --------------------
  // CLIC SUR CELLULE
  // --------------------
  const handleCellClick = (rowId, colId, e) => {
    // Double clic → sous-éditeur
    if (e && e.detail === 2) {
      const key = `${rowId}-${colId}`;
      setActiveCell(key);
      const existing = cellDiagrams[key];
      setSubNodes(existing?.nodes || []);
      setSubEdges(existing?.edges || []);
      return;
    }

    if (editorMode === "cell") {
      return;
    }

    if (editorMode === "arrow-info" || editorMode === "arrow-decision") {
      if (!arrowStart) {
        setArrowStart({ rowId, colId });
        showNotification("Cellule de départ sélectionnée.", "info");
        return;
      }

      const newArrowId =
        arrows.length > 0 ? Math.max(...arrows.map((a) => a.id)) + 1 : 1;

      const newArrow = {
        id: newArrowId,
        start: { ...arrowStart },
        end: { rowId, colId },
        created: Date.now(),
        type: editorMode === "arrow-info" ? "information" : "decision",
        color: editorMode === "arrow-info" ? "#EF4444" : "#3B82F6",
        thickness: editorMode === "arrow-info" ? 2 : 8,
      };

      setArrows((prev) => [...prev, newArrow]);
      setArrowStart(null);
      showNotification(
        `Flèche ${newArrow.type} ajoutée avec succès`,
        "success"
      );
    }
  };

  // --------------------
  // HIGHLIGHT / CONNECTÉ
  // --------------------
  const highlightAvailableCells = (sourceRowId, sourceColId) => {
    const allCells = document.querySelectorAll(".grid-cell");
    allCells.forEach((cell) => {
      const cellId = cell.id;
      if (!cellId || !cellId.startsWith("cell-")) return;

      const [, rowIdStr, colIdStr] = cellId.split("-");
      const rowId = parseInt(rowIdStr, 10);
      const colId = parseInt(colIdStr, 10);

      if (rowId === sourceRowId && colId === sourceColId) return;

      const sourceColumn = columns.find((col) => col.id === sourceColId);
      const targetColumn = columns.find((col) => col.id === colId);

      if (sourceColumn?.isPseudoFunction && targetColumn?.isPseudoFunction) {
        cell.classList.add("connection-not-allowed");
      } else {
        cell.classList.add("connection-allowed");
      }
    });
  };

  const clearAvailableCells = () => {
    const allCells = document.querySelectorAll(".grid-cell");
    allCells.forEach((cell) => {
      cell.classList.remove("connection-allowed", "connection-not-allowed");
    });
  };

  const highlightConnectedCells = (rowId, colId, type) => {
    setConnectedCells((prev) => ({
      ...prev,
      [`${rowId}-${colId}`]: type,
    }));
  };

  const clearConnectedCells = () => {
    setConnectedCells({});
  };

  // --------------------
  // FLÈCHES
  // --------------------
  const deleteArrow = (arrowId) => {
    setArrows((prev) => prev.filter((a) => a.id !== arrowId));
    showNotification("Relation supprimée", "success");
  };

  const changeArrowType = (arrowId, type) => {
    const updatedArrows = arrows.map((a) => {
      if (a.id === arrowId) {
        let color = "#3B82F6";
        let thickness = 2;

        if (type === "information") {
          color = "#EF4444";
        } else if (type === "vertical") {
          color = "#10B981";
          thickness = 5;
        }

        return { ...a, type, color, thickness };
      }
      return a;
    });

    setArrows(updatedArrows);
    showNotification(`Type de flèche modifié : ${type}`, "info");
  };

  const addExternalSource = (title, position) => {
    const newId =
      externalSources.length > 0
        ? Math.max(...externalSources.map((s) => s.id)) + 1
        : 1;

    setExternalSources((prev) => [...prev, { id: newId, title, position }]);
    showNotification(`Source '${title}' ajoutée`, "success");
  };

  // --------------------
  // EXPORTS
  // --------------------
  const exportAsImage = () => {
    if (!gridRef.current) {
      showNotification("Erreur: Référence à la grille non disponible", "error");
      return;
    }

    const format = window.confirm(
      "Souhaitez-vous exporter en PDF ?\nCliquez sur 'OK' pour PDF, ou 'Annuler' pour PNG."
    )
      ? "pdf"
      : "png";

    setIsExporting(true);
    showNotification(
      `Préparation de l'exportation en ${format.toUpperCase()}...`,
      "info"
    );

    const currentZoom = zoomLevel;
    setZoomLevel(100);

    setTimeout(() => {
      try {
        const originalElement = document.querySelector(".grai-grid");
        if (!originalElement)
          throw new Error("Grille GRAI introuvable dans le DOM");

        const container = document.createElement("div");
        container.style.position = "fixed";
        container.style.top = "0";
        container.style.left = "0";
        container.style.width = `${originalElement.scrollWidth}px`;
        container.style.height = `${originalElement.scrollHeight}px`;
        container.style.backgroundColor = "white";
        container.style.zIndex = "-9999";
        container.style.overflow = "hidden";

        const clone = originalElement.cloneNode(true);
        clone.style.transform = "none";
        clone.style.border = "1px solid #ddd";
        clone.style.boxShadow = "none";

        container.appendChild(clone);
        document.body.appendChild(container);

        setTimeout(() => {
          html2canvas(clone, {
            backgroundColor: "#ffffff",
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
          })
            .then((canvas) => {
              document.body.removeChild(container);
              setZoomLevel(currentZoom);

              try {
                const fileName = `decisional-modele-${new Date()
                  .toISOString()
                  .slice(0, 10)}`;

                if (format === "pdf") {
                  const imgData = canvas.toDataURL("image/png");
                  const pdf = new jsPDF({
                    orientation: "landscape",
                    unit: "px",
                    format: [canvas.width, canvas.height],
                  });
                  pdf.addImage(
                    imgData,
                    "PNG",
                    0,
                    0,
                    canvas.width,
                    canvas.height
                  );
                  pdf.save(`${fileName}.pdf`);
                  showNotification("Exportation PDF réussie !", "success");
                } else {
                  const dataUrl = canvas.toDataURL("image/png");
                  const link = document.createElement("a");
                  link.href = dataUrl;
                  link.download = `${fileName}.png`;
                  link.click();
                  showNotification("Exportation PNG réussie !", "success");
                }

                setIsExporting(false);
              } catch (err) {
                console.error("Erreur lors de l'exportation:", err);
                setIsExporting(false);
                showNotification("Erreur lors de l'exportation", "error");
              }
            })
            .catch((err) => {
              console.error("Erreur html2canvas:", err);
              document.body.removeChild(container);
              setIsExporting(false);
              setZoomLevel(currentZoom);
              showNotification(
                "Erreur lors de la génération du rendu",
                "error"
              );
            });
        }, 500);
      } catch (error) {
        console.error("Erreur lors de la préparation de l'exportation:", error);
        setIsExporting(false);
        setZoomLevel(currentZoom);
        showNotification(
          "Erreur lors de la préparation de l'exportation",
          "error"
        );
      }
    }, 300);
  };

  const exportAsImageAlternative = () => {
    if (!gridRef.current) {
      showNotification("Erreur: Référence à la grille non disponible", "error");
      return;
    }

    setIsExporting(true);
    showNotification(
      "Préparation de l'exportation (méthode alternative)...",
      "info"
    );

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const gridElement = document.querySelector(".grai-grid");
      if (!gridElement) {
        throw new Error("Élément de grille introuvable");
      }

      const width = gridElement.offsetWidth;
      const height = gridElement.offsetHeight;
      canvas.width = width * 2;
      canvas.height = height * 2;
      ctx.scale(2, 2);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = "#dddddd";
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, width, height);

      ctx.fillStyle = "#333333";
      ctx.font = "bold 18px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Grille GRAI", width / 2, 30);

      const cellWidth = (width - 100) / columns.length;
      const cellHeight = 40;
      const startX = 100;
      const startY = 60;

      ctx.fillStyle = "#f0f0f0";
      ctx.fillRect(0, startY, startX, cellHeight);

      columns.forEach((column, colIndex) => {
        const x = startX + colIndex * cellWidth;
        ctx.fillStyle = "#f0f0f0";
        ctx.fillRect(x, startY, cellWidth, cellHeight);
        ctx.strokeStyle = "#cccccc";
        ctx.strokeRect(x, startY, cellWidth, cellHeight);
        ctx.fillStyle = "#333333";
        ctx.font = "bold 12px Arial";
        ctx.textAlign = "center";
        ctx.fillText(
          column.title.length > 15
            ? column.title.substring(0, 15) + "..."
            : column.title,
          x + cellWidth / 2,
          startY + 25
        );
      });

      rows.forEach((row, rowIndex) => {
        const y = startY + (rowIndex + 1) * cellHeight;

        ctx.fillStyle = "#f0f0f0";
        ctx.fillRect(0, y, startX, cellHeight);
        ctx.strokeStyle = "#cccccc";
        ctx.strokeRect(0, y, startX, cellHeight);

        ctx.fillStyle = "#333333";
        ctx.font = "bold 12px Arial";
        ctx.textAlign = "center";
        ctx.fillText(row.title, startX / 2, y + 25);

        columns.forEach((column, colIndex) => {
          const x = startX + colIndex * cellWidth;

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(x, y, cellWidth, cellHeight);
          ctx.strokeStyle = "#cccccc";
          ctx.strokeRect(x, y, cellWidth, cellHeight);

          const cellContent = cells[`${row.id}-${column.id}`]?.content || "";

          ctx.fillStyle = "#333333";
          ctx.font = "11px Arial";
          ctx.textAlign = "center";

          const maxCharsPerLine = 20;
          let yOffset = 15;

          if (cellContent.length <= maxCharsPerLine) {
            ctx.fillText(cellContent, x + cellWidth / 2, y + yOffset);
          } else {
            let remainingText = cellContent;
            while (remainingText.length > 0 && yOffset < cellHeight - 5) {
              let lineText;
              if (remainingText.length > maxCharsPerLine) {
                let cutIndex = remainingText
                  .substring(0, maxCharsPerLine)
                  .lastIndexOf(" ");
                if (cutIndex === -1) cutIndex = maxCharsPerLine;
                lineText = remainingText.substring(0, cutIndex);
                remainingText = remainingText.substring(cutIndex + 1);
              } else {
                lineText = remainingText;
                remainingText = "";
              }

              ctx.fillText(lineText, x + cellWidth / 2, y + yOffset);
              yOffset += 15;
            }
          }
        });
      });

      arrows.forEach((arrow) => {
        const startRow = rows.findIndex((r) => r.id === arrow.start.rowId);
        const startCol = columns.findIndex((c) => c.id === arrow.start.colId);
        const endRow = rows.findIndex((r) => r.id === arrow.end.rowId);
        const endCol = columns.findIndex((c) => c.id === arrow.end.colId);

        if (startRow === -1 || startCol === -1 || endRow === -1 || endCol === -1)
          return;

        const startX1 = startX + startCol * cellWidth + cellWidth / 2;
        const startY1 =
          startY + (startRow + 1) * cellHeight + cellHeight / 2;
        const endX1 = startX + endCol * cellWidth + cellWidth / 2;
        const endY1 = startY + (endRow + 1) * cellHeight + cellHeight / 2;

        ctx.beginPath();
        ctx.moveTo(startX1, startY1);
        ctx.lineTo(endX1, endY1);
        ctx.strokeStyle =
          arrow.color ||
          (arrow.type === "decision" ? "#3b82f6" : "#ef4444");
        ctx.lineWidth = arrow.thickness || 2;
        ctx.stroke();

        const angle = Math.atan2(endY1 - startY1, endX1 - startX1);
        const arrowSize = arrow.type === "vertical" ? 12 : 8;

        ctx.beginPath();
        ctx.moveTo(endX1, endY1);
        ctx.lineTo(
          endX1 - arrowSize * Math.cos(angle - Math.PI / 6),
          endY1 - arrowSize * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
          endX1 - arrowSize * Math.cos(angle + Math.PI / 6),
          endY1 - arrowSize * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fillStyle = arrow.type === "decision" ? "#3b82f6" : "#ef4444";
        ctx.fill();
      });

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `grille-grai-${new Date()
        .toISOString()
        .slice(0, 10)}-alt.png`;
      link.click();

      setIsExporting(false);
      showNotification(
        "Exportation alternative réussie! L'image a été téléchargée.",
        "success"
      );
    } catch (error) {
      console.error("Erreur lors de l'exportation alternative:", error);
      setIsExporting(false);
      showNotification("Erreur lors de l'exportation alternative", "error");
    }
  };

  const exportWithOptions = () => {
    if (
      window.confirm(
        "Choisir une méthode d'exportation:\n\n- OK pour la méthode standard\n- Annuler pour la méthode alternative (recommandée si l'image est blanche)"
      )
    ) {
      exportAsImage();
    } else {
      exportAsImageAlternative();
    }
  };

  // --------------------
  // SAUVEGARDE / CHARGEMENT
  // --------------------
  const saveGrid = () => {
    const gridData = {
      columns,
      rows,
      cells,
      arrows,
      externalSources,
      cellDiagrams,
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(gridData));
      showNotification("Grille sauvegardée avec succès", "success");
      console.log(`[${instanceId}] Grid saved`, gridData);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde", error);
      showNotification("Erreur lors de la sauvegarde", "error");
    }
  };

  const loadGrid = () => {
    try {
      setIsLoading(true);
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setColumns(parsedData.columns || []);
        setRows(parsedData.rows || []);
        setCells(parsedData.cells || {});
        setArrows(parsedData.arrows || []);
        setExternalSources(parsedData.externalSources || []);
        setCellDiagrams(parsedData.cellDiagrams || {});
        showNotification("Grille chargée avec succès", "success");
      } else {
        showNotification("Aucune grille sauvegardée trouvée", "info");
      }

      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Erreur lors du chargement", error);
      showNotification("Erreur lors du chargement des données", "error");
      setIsLoading(false);
    }
  };

  const loadEmptyTemplate = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir créer un nouveau modèle? Toutes les données non sauvegardées seront perdues."
      )
    ) {
      setIsLoading(true);

      setColumns([]);
      setRows([]);
      setCells({});
      setArrows([]);
      setExternalSources([]);
      setCellDiagrams({});

      setTimeout(() => {
        setIsLoading(false);
        showNotification("Nouveau modèle créé", "success");
      }, 300);
    }
  };

  // Chargement initial
  useEffect(() => {
    setIsLoading(true);

    const savedData = localStorage.getItem(storageKey);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setColumns(parsedData.columns || []);
        setRows(parsedData.rows || []);
        setCells(parsedData.cells || {});
        setArrows(parsedData.arrows || []);
        setExternalSources(parsedData.externalSources || []);
        setCellDiagrams(parsedData.cellDiagrams || {});
      } catch (error) {
        console.error("Error loading saved data", error);
        showNotification(
          "Erreur lors du chargement des données sauvegardées",
          "error"
        );
      }
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, [storageKey]); // 🔑 dépend de la clé

  // --------------------
  // ZOOM
  // --------------------
  const handleZoomIn = () => {
    if (zoomLevel < 200) {
      setZoomLevel((prevZoom) => Math.min(prevZoom + 10, 200));
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 50) {
      setZoomLevel((prevZoom) => Math.max(prevZoom - 10, 50));
    }
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const handleWheel = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) handleZoomIn();
        else handleZoomOut();
      }
    },
    [] // on utilise directement handleZoomIn/out qui lisent l'état
  );

  // --------------------
  // TOOLTIP / NOTIFS
  // --------------------
  const handleShowTooltip = (e, text) => {
    if (!text) return;

    if (typeof e === "string") {
      text = e;
      setTooltip({
        visible: true,
        text,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      return;
    }

    if (!e || !e.currentTarget) {
      setTooltip({
        visible: true,
        text,
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.bottom + 10,
    });
  };

  const handleHideTooltip = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // --------------------
  // MODES ÉDITEUR
  // --------------------
  const changeEditorMode = (mode) => {
    setEditorMode(mode);

    if (mode !== "arrow-info" && mode !== "arrow-decision") {
      setArrowStart(null);
      clearAvailableCells();
    }

    const container = document.querySelector(".grai-editor-container");
    if (container) {
      container.classList.remove(
        "cell-mode",
        "arrow-info-mode",
        "arrow-decision-mode",
        "delete-mode"
      );
      container.classList.add(`${mode}-mode`);
    }

    const gridElement = document.querySelector(".grai-grid");
    if (gridElement) {
      if (mode === "arrow-info" || mode === "arrow-decision") {
        gridElement.style.cursor = "crosshair";
      } else if (mode === "delete") {
        gridElement.style.cursor = "not-allowed";
      } else {
        gridElement.style.cursor = "default";
      }
    }
  };

  // --------------------
  // DRAG POUR DÉFILEMENT
  // --------------------
  const handleMouseDown = useCallback((e) => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      if (gridContentRef.current) {
        setIsDragging(true);
        setStartPoint({
          x: e.clientX,
          y: e.clientY,
        });
        setScrollPosition({
          x: gridContentRef.current.scrollLeft,
          y: gridContentRef.current.scrollTop,
        });
        gridContentRef.current.classList.add("can-scroll");
      }
    }
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !gridContentRef.current) return;

      const dx = e.clientX - startPoint.x;
      const dy = e.clientY - startPoint.y;

      gridContentRef.current.scrollLeft = scrollPosition.x - dx;
      gridContentRef.current.scrollTop = scrollPosition.y - dy;
    },
    [isDragging, startPoint, scrollPosition]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging && gridContentRef.current) {
      setIsDragging(false);
      gridContentRef.current.classList.remove("can-scroll");
    }
  }, [isDragging]);

  useEffect(() => {
    if (gridRef.current) {
      const gridElement = gridRef.current;
      gridElement.addEventListener("wheel", handleWheel, { passive: false });
      gridElement.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        gridElement.removeEventListener("wheel", handleWheel);
        gridElement.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [handleWheel, handleMouseDown, handleMouseMove, handleMouseUp]);

  // --------------------
  // REDIMENSIONNEMENT COLONNES
  // --------------------
  useEffect(() => {
    let isResizing = false;
    let currentColumn = null;
    let startX = 0;
    let startWidth = 0;

    const initColumnResize = () => {
      const headerCells = document.querySelectorAll(
        ".grid-header-cell.column-header"
      );

      headerCells.forEach((cell) => {
        const resizer = document.createElement("div");
        resizer.className = "column-resizer";
        resizer.style.position = "absolute";
        resizer.style.top = "0";
        resizer.style.right = "0";
        resizer.style.width = "8px";
        resizer.style.height = "100%";
        resizer.style.cursor = "col-resize";
        resizer.style.zIndex = "20";

        cell.style.position = "relative";
        cell.appendChild(resizer);

        resizer.addEventListener("mousedown", (e) => {
          isResizing = true;
          currentColumn = cell;
          startX = e.pageX;
          startWidth = cell.offsetWidth;

          document.body.classList.add("column-resizing");

          e.preventDefault();
          e.stopPropagation();
        });
      });

      const onMouseMove = (e) => {
        if (!isResizing || !currentColumn) return;

        const width = startWidth + (e.pageX - startX);
        if (width >= 100) {
          const columnIndex = Array.from(
            currentColumn.parentNode.children
          ).indexOf(currentColumn);
          const columnCells = document.querySelectorAll(
            `.grid-row > *:nth-child(${columnIndex + 1})`
          );

          currentColumn.style.width = `${width}px`;
          currentColumn.style.minWidth = `${width}px`;

          columnCells.forEach((cell) => {
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
          });
        }
      };

      const onMouseUp = () => {
        if (isResizing) {
          isResizing = false;
          currentColumn = null;
          document.body.classList.remove("column-resizing");
        }
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };
    };

    if (gridRef.current && columns.length > 0 && rows.length > 0) {
      setTimeout(initColumnResize, 500);
    }

    return () => {
      const resizers = document.querySelectorAll(".column-resizer");
      resizers.forEach((resizer) => {
        resizer.parentNode?.removeChild(resizer);
      });
    };
  }, [columns.length, rows.length]);

  // --------------------
  // STYLE ZOOM
  // --------------------
  const gridStyle = {
    transform: `scale(${zoomLevel / 100})`,
    transformOrigin: "top left",
    transition: "transform 0.2s ease",
  };

  // --------------------
  // CLASSES CONNECTÉS + ERREURS
  // --------------------
  const connectedCellsClasses = useMemo(() => {
    const classes = {};

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        if (error.elements) {
          error.elements.forEach((elementId) => {
            if (elementId.startsWith("cell-")) {
              classes[elementId.replace("cell-", "")] = "validation-error";
            }
          });
        }
      });
    }

    arrows.forEach((arrow) => {
      const startKey = `${arrow.start.rowId}-${arrow.start.colId}`;
      const endKey = `${arrow.end.rowId}-${arrow.end.colId}`;

      const hasError = validationErrors.some(
        (error) => error.arrowId === arrow.id
      );

      classes[startKey] = `${classes[startKey] || ""} connected-cell arrow-start${
        hasError ? " validation-error" : ""
      }`.trim();
      classes[endKey] = `${classes[endKey] || ""} connected-cell arrow-end${
        hasError ? " validation-error" : ""
      }`.trim();
    });

    return classes;
  }, [arrows, connectedCells, validationErrors]);

  const getColumnHeaderClass = useCallback(
    (columnId) => {
      const hasError = validationErrors.some(
        (error) =>
          error.elements && error.elements.includes(`header-col-${columnId}`)
      );
      return hasError ? "validation-error" : "";
    },
    [validationErrors]
  );

  const getRowHeaderClass = useCallback(
    (rowId) => {
      const hasError = validationErrors.some(
        (error) =>
          error.elements && error.elements.includes(`header-row-${rowId}`)
      );
      return hasError ? "validation-error" : "";
    },
    [validationErrors]
  );

  const getArrowClass = useCallback(
    (arrowId) => {
      const hasError = validationErrors.some((error) => error.arrowId === arrowId);
      return hasError ? "validation-error" : "";
    },
    [validationErrors]
  );

  // --------------------
  // VALIDATION GRAI
  // --------------------
  const validateGraiRules = useCallback(() => {
    const errors = [];

    // CMG1
    arrows.forEach((arrow) => {
      if (
        arrow.type === "decision" &&
        arrow.start.rowId === arrow.end.rowId &&
        arrow.start.colId === arrow.end.colId
      ) {
        errors.push({
          rule: "CMG1",
          message:
            "Un cadre de décision ne peut être reçu par ce même centre",
          elements: [`cell-${arrow.start.rowId}-${arrow.start.colId}`],
          arrowId: arrow.id,
        });
      }
    });

    // CMG2 & CMG4
    arrows.forEach((arrow) => {
      if (arrow.type === "decision") {
        const startColumn = columns.find((col) => col.id === arrow.start.colId);
        const endColumn = columns.find((col) => col.id === arrow.end.colId);

        if (startColumn?.isPseudoFunction && endColumn?.isPseudoFunction) {
          errors.push({
            rule: "CMG2/4",
            message:
              "Un cadre de décision ne peut être émis d'une pseudo-fonction à une autre",
            elements: [
              `cell-${arrow.start.rowId}-${arrow.start.colId}`,
              `cell-${arrow.end.rowId}-${arrow.end.colId}`,
            ],
            arrowId: arrow.id,
          });
        }
      }
    });

    // CMG3
    const externalInfoColumn = columns.find((col) =>
      col.title.toLowerCase().includes("informations externes")
    );
    if (externalInfoColumn && externalInfoColumn.id !== 1) {
      errors.push({
        rule: "CMG3",
        message:
          "La fonction 'informations externes' doit être placée en colonne 1",
        elements: [`header-col-${externalInfoColumn.id}`],
      });
    }

    // CMG5
    const internalInfoColumn = columns.find((col) =>
      col.title.toLowerCase().includes("informations internes")
    );
    if (internalInfoColumn && internalInfoColumn.id !== columns.length) {
      errors.push({
        rule: "CMG5",
        message:
          "La fonction 'informations internes' doit être placée en dernière colonne",
        elements: [`header-col-${internalInfoColumn.id}`],
      });
    }

    // CMG6, 7, 8
    const requiredFunctions = [
      { name: "Gérer les produits", rule: "CMG6" },
      { name: "Gérer les ressources", rule: "CMG7" },
      { name: "Planifier", rule: "CMG8" },
    ];

    requiredFunctions.forEach((func) => {
      if (
        !columns.some((col) =>
          col.title.toLowerCase().includes(func.name.toLowerCase())
        )
      ) {
        errors.push({
          rule: func.rule,
          message: `La fonction '${func.name}' ne peut être absente de la grille`,
          elements: [],
        });
      }
    });

    // CMG9
    for (let i = 1; i < rows.length; i++) {
      if (rows[i].period > rows[i - 1].period) {
        errors.push({
          rule: "CMG9",
          message:
            "Les périodes des niveaux décisionnels doivent être classées par ordre décroissant",
          elements: [`header-row-${rows[i - 1].id}`, `header-row-${rows[i].id}`],
        });
        break;
      }
    }

    // CMG10
    if (rows.length < 3) {
      errors.push({
        rule: "CMG10",
        message:
          "Les niveaux décisionnels ne peuvent être moins de trois dans une grille GRAI",
        elements: [],
      });
    }

    // CMG12
    const internalInfoCount = columns.filter((col) =>
      col.title.toLowerCase().includes("informations internes")
    ).length;
    if (internalInfoCount > 1) {
      const internalInfoColumns = columns
        .filter((col) =>
          col.title.toLowerCase().includes("informations internes")
        )
        .map((col) => `header-col-${col.id}`);

      errors.push({
        rule: "CMG12",
        message:
          "Une grille GRAI ne peut contenir qu'une seule fonction 'informations internes'",
        elements: internalInfoColumns,
      });
    }

    // CMG13
    const externalInfoCount = columns.filter((col) =>
      col.title.toLowerCase().includes("informations externes")
    ).length;
    if (externalInfoCount > 1) {
      const externalInfoColumns = columns
        .filter((col) =>
          col.title.toLowerCase().includes("informations externes")
        )
        .map((col) => `header-col-${col.id}`);

      errors.push({
        rule: "CMG13",
        message:
          "Une grille GRAI ne peut contenir qu'une seule fonction 'informations externes'",
        elements: externalInfoColumns,
      });
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [columns, rows, arrows]);

  useEffect(() => {
    if (automaticValidation) {
      validateGraiRules();
    }
  }, [columns, rows, arrows, automaticValidation, validateGraiRules]);

  // --------------------
  // SUPPRESSION COL / LIGNE
  // --------------------
  const deleteColumn = (columnId) => {
    const columnToDelete = columns.find((col) => col.id === columnId);

    if (columnToDelete?.isObligatory) {
      const columnTitle = columnToDelete.title.toLowerCase();
      const isUniqueObligatory = !columns.some(
        (col) =>
          col.id !== columnId && col.title.toLowerCase() === columnTitle
      );

      if (isUniqueObligatory) {
        alert(
          `Cette fonction est obligatoire selon les règles GRAI et ne peut être supprimée.`
        );
        return;
      }
    }

    if (columnToDelete?.isFixed) {
      alert(
        `Cette fonction ne peut pas être supprimée car elle a une position fixe dans la grille GRAI.`
      );
      return;
    }

    const newColumns = columns
      .filter((col) => col.id !== columnId)
      .map((col, index) => ({
        ...col,
        id: index + 1,
      }));

    setColumns(newColumns);

    const newArrows = arrows.filter(
      (arrow) =>
        arrow.start.colId !== columnId && arrow.end.colId !== columnId
    );
    setArrows(newArrows);

    const newCells = { ...cells };
    rows.forEach((row) => {
      delete newCells[`${row.id}-${columnId}`];
    });
    setCells(newCells);

    if (automaticValidation) {
      setTimeout(() => validateGraiRules(), 0);
    }
  };

  const deleteRow = (rowId) => {
    if (rows.length <= 3) {
      alert(
        "CMG10: Les niveaux décisionnels ne peuvent être moins de trois dans une grille GRAI."
      );
      return;
    }

    const newRows = rows.filter((row) => row.id !== rowId);
    setRows(newRows);

    const newArrows = arrows.filter(
      (arrow) =>
        arrow.start.rowId !== rowId && arrow.end.rowId !== rowId
    );
    setArrows(newArrows);

    const newCells = { ...cells };
    columns.forEach((col) => {
      delete newCells[`${rowId}-${col.id}`];
    });
    setCells(newCells);

    if (automaticValidation) {
      setTimeout(() => validateGraiRules(), 0);
    }
  };

  // --------------------
  // TOOLTIP FLÈCHES
  // --------------------
  const handleArrowHover = useCallback(
    (arrow, position) => {
      const startCell =
        cells[`${arrow.start.rowId}-${arrow.start.colId}`] || {};
      const endCell = cells[`${arrow.end.rowId}-${arrow.end.colId}`] || {};
      const startColumn =
        columns.find((col) => col.id === arrow.start.colId) || {};
      const endColumn =
        columns.find((col) => col.id === arrow.end.colId) || {};
      const startRow =
        rows.find((row) => row.id === arrow.start.rowId) || {};
      const endRow = rows.find((row) => row.id === arrow.end.rowId) || {};

      const typeLabel =
        arrow.type === "decision" ? "Décision" : "Information";

      let tooltipText = `${typeLabel}: ${
        startRow.title || "Horizon"
      } / ${startColumn.title || "Fonction"} → ${
        endRow.title || "Horizon"
      } / ${endColumn.title || "Fonction"}`;

      if (startCell.content && endCell.content) {
        tooltipText += `\nDe: "${startCell.content.substring(
          0,
          30
        )}${startCell.content.length > 30 ? "..." : ""}"`;
        tooltipText += `\nVers: "${endCell.content.substring(
          0,
          30
        )}${endCell.content.length > 30 ? "..." : ""}"`;
      }

      setArrowTooltip({
        visible: true,
        text: tooltipText,
        x: position.x,
        y: position.y,
        arrowId: arrow.id,
      });
    },
    [cells, columns, rows]
  );

  const handleArrowLeave = useCallback(() => {
    setArrowTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  // --------------------
  // NOEUDS SOUS-ÉDITEUR
  // --------------------
  const addNode = (label, style) => {
    const newNode = {
      id: `${Date.now()}`,
      type: "default",
      data: { label, style },
      style,
      position: { x: Math.random() * 400, y: Math.random() * 300 },
    };
    setSubNodes((prev) => [...prev, newNode]);
  };

  // --------------------
  // RENDU
  // --------------------
  return (
    <div
      className={`grai-editor-container ${isExporting ? "exporting" : ""}`}
      data-instance-id={instanceId}
    >
      <div className="grai-editor-header">
        <h1 className="grai-editor-title">{title}</h1>

        <Toolbar
          editorMode={editorMode}
          setEditorMode={changeEditorMode}
          addColumn={addColumn}
          addRow={addRow}
          addExternalSource={addExternalSource}
          exportAsImage={exportWithOptions}
          saveGrid={saveGrid}
          loadGrid={loadGrid}
          loadEmptyTemplate={loadEmptyTemplate}
          zoomLevel={zoomLevel}
          onShowTooltip={handleShowTooltip}
          onHideTooltip={handleHideTooltip}
          validateGrid={validateGraiRules}
          validationErrors={validationErrors}
          showValidationErrors={showValidationErrors}
          setShowValidationErrors={setShowValidationErrors}
          automaticValidation={automaticValidation}
          setAutomaticValidation={setAutomaticValidation}
        />
      </div>

      <div
        className={`grai-grid-container ${isDragging ? "dragging" : ""}`}
        ref={gridRef}
      >
        {isLoading && (
          <div className="grid-loading">
            <div className="grid-loading-spinner"></div>
            <p>Chargement de la grille...</p>
          </div>
        )}

        {showValidationErrors && validationErrors.length > 0 && (
          <div className="grai-validation-errors">
            <h3>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Erreurs de validation ({validationErrors.length})
            </h3>
            <ul className="validation-error-list">
              {validationErrors.map((error, index) => (
                <li key={index} className="validation-error-item">
                  <strong>{error.rule}:</strong> {error.message}
                </li>
              ))}
            </ul>
            <div className="validation-actions">
              <button
                className="toolbar-button"
                onClick={() => setShowValidationErrors(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        <div className="external-sources-top">
          {externalSources
            .filter((source) => source.position === "top")
            .map((source) => (
              <ExternalSource
                key={source.id}
                source={source}
                onDelete={() => {
                  setExternalSources((prev) =>
                    prev.filter((s) => s.id !== source.id)
                  );
                }}
                editorMode={editorMode}
              />
            ))}
        </div>

        <div className="grai-grid-sides">
          <div className="external-sources-left">
            {externalSources
              .filter((source) => source.position === "left")
              .map((source) => (
                <ExternalSource
                  key={source.id}
                  source={source}
                  onDelete={() => {
                    setExternalSources((prev) =>
                      prev.filter((s) => s.id !== source.id)
                    );
                  }}
                  editorMode={editorMode}
                />
              ))}
          </div>

          <div className="grai-grid-content" ref={gridContentRef}>
            <div className="grai-grid" style={gridStyle}>
              <div className="grid-header">
                <div className="header-corner">Horizon / Fonction</div>

                {columns.map((column) => (
                  <GridHeader
                    key={column.id}
                    header={column}
                    isColumn={true}
                    onUpdate={(title) => {
                      setColumns((prev) =>
                        prev.map((col) =>
                          col.id === column.id ? { ...col, title } : col
                        )
                      );
                    }}
                    onDelete={() => {
                      if (
                        window.confirm(
                          `Supprimer la fonction "${column.title}" ?`
                        )
                      ) {
                        deleteColumn(column.id);
                      }
                    }}
                    editorMode={editorMode}
                    className={getColumnHeaderClass(column.id)}
                    style={{
                      backgroundColor: column.color || "#3B82F6",
                      color: "white",
                      borderTop: `4px solid ${
                        column.color || "#3B82F6"
                      }`,
                    }}
                  />
                ))}
              </div>

              {rows.map((row) => (
                <div className="grid-row" key={row.id}>
                  <GridHeader
                    header={row}
                    isColumn={false}
                    onUpdate={(data) => {
                      setRows((prev) =>
                        prev.map((r) =>
                          r.id === row.id ? { ...r, ...data } : r
                        )
                      );
                    }}
                    onDelete={() => {
                      if (
                        window.confirm(
                          `Supprimer l'horizon "${row.title}" ?`
                        )
                      ) {
                        deleteRow(row.id);
                      }
                    }}
                    editorMode={editorMode}
                    className={getRowHeaderClass(row.id)}
                  />

                  {columns.map((column) => (
                    <GridCell
                      key={`${row.id}-${column.id}`}
                      id={`cell-${row.id}-${column.id}`}
                      rowId={row.id}
                      colId={column.id}
                      content={
                        cells[`${row.id}-${column.id}`]?.content || ""
                      }
                      onClick={(e) =>
                        handleCellClick(row.id, column.id, e)
                      }
                      onUpdate={(content) =>
                        updateCell(row.id, column.id, content)
                      }
                      editorMode={editorMode}
                      isStart={
                        arrowStart &&
                        arrowStart.rowId === row.id &&
                        arrowStart.colId === column.id
                      }
                      className={
                        connectedCellsClasses[`${row.id}-${column.id}`]
                      }
                      onOpenSubEditor={(r, c) => {
                        const key = `${r}-${c}`;
                        setActiveCell(key);
                        const existing = cellDiagrams[key];
                        setSubNodes(existing?.nodes || []);
                        setSubEdges(existing?.edges || []);
                      }}
                    />
                  ))}
                </div>
              ))}

              <ArrowManager
                ref={arrowManagerRef}
                arrows={arrows}
                columns={columns}
                rows={rows}
                containerRef={gridContentRef}
                zoomLevel={zoomLevel}
                editorMode={editorMode}
                deleteArrow={deleteArrow}
                changeArrowType={changeArrowType}
                getArrowClass={(arrow) =>
                  validationErrors.find((e) => e.arrowId === arrow.id)
                    ? "validation-error"
                    : ""
                }
                onArrowHover={(arrow, position) => {
                  const startCell = columns.find(
                    (c) => c.id === arrow.start.colId
                  )?.title;
                  const endCell = columns.find(
                    (c) => c.id === arrow.end.colId
                  )?.title;
                  const startRow = rows.find(
                    (r) => r.id === arrow.start.rowId
                  )?.title;
                  const endRow = rows.find(
                    (r) => r.id === arrow.end.rowId
                  )?.title;

                  const arrowTypeLabel =
                    arrow.type === "decision"
                      ? "Décision (Bleu)"
                      : arrow.type === "information"
                      ? "Information (Rouge)"
                      : "Relation verticale (Vert)";
                  const tooltipText = `${arrowTypeLabel}: ${startCell} (${startRow}) → ${endCell} (${endRow})`;

                  setArrowTooltip({
                    visible: true,
                    text: tooltipText,
                    x: position.x,
                    y: position.y,
                    arrowId: arrow.id,
                  });
                }}
                onArrowLeave={() => {
                  setArrowTooltip((prev) => ({
                    ...prev,
                    visible: false,
                  }));
                }}
              />
            </div>
          </div>

          <div className="external-sources-right">
            {externalSources
              .filter((source) => source.position === "right")
              .map((source) => (
                <ExternalSource
                  key={source.id}
                  source={source}
                  onDelete={() => {
                    setExternalSources((prev) =>
                      prev.filter((s) => s.id !== source.id)
                    );
                  }}
                  editorMode={editorMode}
                />
              ))}
          </div>
        </div>

        <div className="external-sources-bottom">
          {externalSources
            .filter((source) => source.position === "bottom")
            .map((source) => (
              <ExternalSource
                key={source.id}
                source={source}
                onDelete={() => {
                  setExternalSources((prev) =>
                    prev.filter((s) => s.id !== source.id)
                  );
                }}
                editorMode={editorMode}
              />
            ))}
        </div>

        <div className="grai-zoom-controls">
          <button
            className="zoom-button"
            onClick={handleZoomOut}
            disabled={zoomLevel <= 50}
            onMouseEnter={(e) => handleShowTooltip(e, "Réduire")}
            onMouseLeave={handleHideTooltip}
          >
            −
          </button>
          <span className="zoom-level">{zoomLevel}%</span>
          <button
            className="zoom-button"
            onClick={handleZoomReset}
            onMouseEnter={(e) => handleShowTooltip(e, "Réinitialiser")}
            onMouseLeave={handleHideTooltip}
          >
            ⊙
          </button>
          <button
            className="zoom-button"
            onClick={handleZoomIn}
            disabled={zoomLevel >= 200}
            onMouseEnter={(e) => handleShowTooltip(e, "Agrandir")}
            onMouseLeave={handleHideTooltip}
          >
            +
          </button>
        </div>
      </div>

      {isExporting && (
        <div className="export-loading">
          <div className="export-loading-content">
            <div className="export-loading-spinner"></div>
            <h3>Préparation de l'exportation</h3>
            <p>
              Veuillez patienter pendant que nous préparons votre grille GRAI
              pour l'exportation...
            </p>
          </div>
        </div>
      )}

      {notification && (
        <div className={`grai-notification ${notification.type}`}>
          <div className="notification-content">
            {notification.type === "success" && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 18.333C14.6024 18.333 18.333 14.6024 18.333 10C18.333 5.39763 14.6024 1.66667 10 1.66667C5.39762 1.66667 1.66666 5.39763 1.66666 10C1.66666 14.6024 5.39762 18.333 10 18.333Z"
                  stroke="#10B981"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66666 10L9.16666 12.5L13.3333 8.33334"
                  stroke="#10B981"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {notification.type === "error" && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 18.333C14.6024 18.333 18.333 14.6024 18.333 10C18.333 5.39763 14.6024 1.66667 10 1.66667C5.39762 1.66667 1.66666 5.39763 1.66666 10C1.66666 14.6024 5.39762 18.333 10 18.333Z"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.5 7.5L7.5 12.5"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 7.5L12.5 12.5"
                  stroke="#EF4444"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {notification.type === "info" && (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 18.333C14.6024 18.333 18.333 14.6024 18.333 10C18.333 5.39763 14.6024 1.66667 10 1.66667C5.39762 1.66667 1.66666 5.39763 1.66666 10C1.66666 14.6024 5.39762 18.333 10 18.333Z"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 6.66667V10"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 13.333H10.0083"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {notification.message}
          </div>
        </div>
      )}

      {/* 🧩 Sous-éditeur de diagrammes */}
      {activeCell && (
        <div className="subeditor-overlay">
          <div className="subeditor-container">
            <div className="subeditor-header">
              <h3>Diagramme pour la cellule {activeCell}</h3>
              <button
                className="subeditor-close"
                onClick={() => setActiveCell(null)}
              >
                ✕
              </button>
            </div>

            <div className="subeditor-toolbar">
              <button
                onClick={() =>
                  addNode("Activité", {
                    background: "#e0f2fe",
                    border: "2px solid #2563eb",
                    borderRadius: 6,
                    width: 140,
                    height: 60,
                  })
                }
              >
                🟦 Activité
              </button>

              <button
                onClick={() =>
                  addNode("Décision", {
                    background: "#fef9c3",
                    border: "2px solid #eab308",
                    shape: "diamond",
                    width: 100,
                    height: 100,
                  })
                }
              >
                🔶 Décision
              </button>

              <button
                onClick={() =>
                  addNode("Donnée", {
                    background: "#ecfdf5",
                    border: "2px solid #10b981",
                    shape: "parallelogram",
                    width: 150,
                    height: 60,
                  })
                }
              >
                📄 Donnée
              </button>

              <button
                onClick={() =>
                  addNode("Début", {
                    background: "#dcfce7",
                    border: "2px solid #16a34a",
                    borderRadius: "50%",
                    width: 70,
                    height: 70,
                  })
                }
              >
                🟢 Début
              </button>

              <button
                onClick={() =>
                  addNode("Fin", {
                    background: "#fee2e2",
                    border: "2px solid #ef4444",
                    borderRadius: "50%",
                    width: 70,
                    height: 70,
                  })
                }
              >
                🔴 Fin
              </button>

              <button
                onClick={() =>
                  addNode("Commentaire", {
                    background: "#f3f4f6",
                    border: "1px dashed #6b7280",
                    padding: "8px 12px",
                    fontStyle: "italic",
                    color: "#374151",
                  })
                }
              >
                💬 Commentaire
              </button>

              <button
                onClick={() =>
                  setSubEdges((prev) => prev.slice(0, -1))
                }
                style={{ color: "red" }}
              >
                🗑 Supprimer flèche
              </button>

              <button
                onClick={() => {
                  if (window.confirm("Effacer tout le diagramme ?")) {
                    setSubNodes([]);
                    setSubEdges([]);
                  }
                }}
                style={{ color: "darkred" }}
              >
                ❌ Tout effacer
              </button>
            </div>

            <div className="subeditor-body">
              <ReactFlow
                nodes={subNodes.map((node) => ({
                  ...node,
                  type: "editable",
                }))}
                edges={subEdges}
                onNodesChange={(changes) =>
                  setSubNodes((nds) => applyNodeChanges(changes, nds))
                }
                onEdgesChange={(changes) =>
                  setSubEdges((eds) => applyEdgeChanges(changes, eds))
                }
                onConnect={(params) =>
                  setSubEdges((eds) => [
                    ...eds,
                    {
                      ...params,
                      type: "orthogonal",
                      markerEnd: "url(#arrow)",
                      style: { stroke: "#111", strokeWidth: 1.6 },
                    },
                  ])
                }
                fitView
                panOnDrag
                zoomOnScroll
                nodesDraggable
                nodesConnectable
                elementsSelectable
                defaultEdgeOptions={{
                  type: "orthogonal",
                  markerEnd: "url(#arrow)",
                  style: { stroke: "#111", strokeWidth: 1.6 },
                }}
                nodeTypes={{
                  editable: ({ id, data }) => {
                    const [isEditing, setIsEditing] = React.useState(false);
                    const [text, setText] = React.useState(
                      data.label || ""
                    );

                    const handleSave = () => {
                      setIsEditing(false);
                      setSubNodes((prev) =>
                        prev.map((n) =>
                          n.id === id
                            ? {
                                ...n,
                                data: { ...n.data, label: text },
                              }
                            : n
                        )
                      );
                    };

                    const style = {
                      ...(data.style || {}),
                      cursor: isEditing ? "text" : "move",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      userSelect: "none",
                      position: "relative",
                      textAlign: "center",
                      overflow: "hidden",
                    };

                    if (data.style?.shape === "diamond") {
                      style.clipPath =
                        "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)";
                    } else if (data.style?.shape === "parallelogram") {
                      style.clipPath =
                        "polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)";
                    }

                    return (
                      <div
                        style={style}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setIsEditing(true);
                        }}
                      >
                        {isEditing ? (
                          <input
                            value={text}
                            onChange={(e) =>
                              setText(e.target.value)
                            }
                            onBlur={handleSave}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleSave()
                            }
                            autoFocus
                            style={{
                              width: "90%",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              textAlign: "center",
                              fontWeight: "inherit",
                              color: "inherit",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span>{text}</span>
                        )}
                      </div>
                    );
                  },
                }}
                style={{ width: "100%", height: "100%" }}
              >
                <svg
                  style={{
                    height: 0,
                    width: 0,
                    position: "absolute",
                  }}
                >
                  <defs>
                    <marker
                      id="arrow"
                      markerWidth="12"
                      markerHeight="12"
                      refX="12"
                      refY="6"
                      orient="auto"
                      markerUnits="strokeWidth"
                    >
                      <path
                        d="M0,0 L12,6 L0,12 L4,6 z"
                        fill="#111"
                      />
                    </marker>
                  </defs>
                </svg>

                <MiniMap />
                <Controls />
                <Background color="#ddd" gap={16} />
              </ReactFlow>
            </div>

            <div className="subeditor-footer">
              <button
                onClick={() => {
                  setCellDiagrams({
                    ...cellDiagrams,
                    [activeCell]: { nodes: subNodes, edges: subEdges },
                  });
                  setActiveCell(null);
                  showNotification(
                    "Sous-diagramme sauvegardé",
                    "success"
                  );
                }}
                className="subeditor-save"
              >
                💾 Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {tooltip.visible && (
        <div
          className="grai-tooltip visible"
          style={{
            top: `${
              Number.isFinite(tooltip.y) ? tooltip.y : 0
            }px`,
            left: `${
              Number.isFinite(tooltip.x) ? tooltip.x : 0
            }px`,
            transform: `translateX(-50%)`,
          }}
        >
          {tooltip.text}
        </div>
      )}

      {arrowTooltip.visible && (
        <div
          className="arrow-tooltip visible"
          style={{
            left: `${
              Number.isFinite(arrowTooltip.x)
                ? arrowTooltip.x + 15
                : 0
            }px`,
            top: `${
              Number.isFinite(arrowTooltip.y)
                ? arrowTooltip.y - 10
                : 0
            }px`,
          }}
        >
          {arrowTooltip.text.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GRAIEditor;
